/**
 * Reminder before saving when brands are taken off a placement or a
 * campaign: where each brand still is ("Still on: Hero · Tue 24 Nov",
 * "Stays in the campaign …"), with an optional tick to remove it there too
 * (Doreen, 2026-10-22). Removing from one never removes from the other by
 * itself — this only reminds.
 */
import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

export interface RemovalItem {
  brandId: string;
  brandName: string;
  /** Plain lines: where the brand still is, what to watch out for. */
  lines: string[];
  /** Optional "also remove it there" tick. */
  option?: string;
}

export default function BrandRemovalCheck({
  open,
  items,
  busy,
  onConfirm,
  onClose,
}: {
  open: boolean;
  items: RemovalItem[];
  busy?: boolean;
  /** Brand ids whose "also remove" tick is on. */
  onConfirm: (alsoRemove: string[]) => void;
  onClose: () => void;
}) {
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  useEffect(() => { if (open) setTicked(new Set()); }, [open]);
  const toggle = (id: string) =>
    setTicked((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={items.length === 1 ? `Removing ${items[0].brandName}` : `Removing ${items.length} brands`}
      maxWidth={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Go back</Button>
          <Button variant="primary" onClick={() => onConfirm([...ticked])} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 14, fontSize: 13, lineHeight: 1.5 }}>
        {items.map((it) => (
          <div key={it.brandId} style={{ padding: '10px 12px', borderRadius: 'var(--r-md)', background: 'var(--bg-subtle)' }}>
            <div style={{ fontWeight: 650, marginBottom: 4 }}>{it.brandName}</div>
            {it.lines.map((l, i) => <div key={i} style={{ color: 'var(--ink-3)' }}>{l}</div>)}
            {it.option && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={ticked.has(it.brandId)} onChange={() => toggle(it.brandId)} />
                {it.option}
              </label>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}
